/**
 * DomainAssignment.jsx
 * data-fit LLC — DecisionFlow
 * Stage 2 CONFIGURE — CONF-04: Domain assignment
 * Branch: feature/compliance-lifecycle-foundation
 * Spec authority: ComponentSpec_CONFIGURE.md (S82) + MasterContext_S88_Close.md
 * Path: ui/src/components/tenant-admin/DomainAssignment.jsx
 *
 * S89 audit patches applied:
 * - PATCH-02, PATCH-03, PATCH-22 — see original for notes.
 * - OI-USEAUTH-PATH: ../../auth/AuthContext.jsx — unverified, remains open.
 * - OI-TENANTADMIN-STAGE12, OI-CONFIGURE-GEO-ENDPOINTS — stubs remain.
 * M040 C2: hardcoded colors → tokens, text-* → text-tok-*, all <td> get text-[rgb(var(--color-text))]
 * S495: Authorization: Bearer token added to all fetch calls (OI-ONBOARDING-AUTH-HEADER-PATTERN)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';

const API_BASE = '/api/v1';

function normalizeResponse(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function getErrorMessage(error) {
  return error?.message || 'Something went wrong.';
}

export default function DomainAssignment({ tenantId, tier, onBack, onContinue }) {
  const { token, user } = useAuth();

  const [users, setUsers] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [segments, setSegments] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [buScope, setBuScope] = useState('');
  const [segmentScope, setSegmentScope] = useState('');
  const [geoScope, setGeoScope] = useState([]);
  const [positionPreviewCount, setPositionPreviewCount] = useState(0);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [regions, setRegions] = useState([]);
  const [subRegions, setSubRegions] = useState([]);
  const [toast, setToast] = useState(null);

  const isEnterprise = tier === 'enterprise';

  const authHeaders = useMemo(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const selectedUser = useMemo(
    () => users.find((item) => String(item.user_id || item.id) === String(selectedUserId)),
    [selectedUserId, users]
  );

  const canSave = useMemo(() => {
    if (!selectedUserId) return false;
    if (isEnterprise && geoScope.some((node) => node.region_id && node.sub_region_id)) return false;
    return true;
  }, [geoScope, isEnterprise, selectedUserId]);

  const fetchInitialData = useCallback(async () => {
    setLoadError(null);
    try {
      const [usersResponse, buResponse, segmentResponse] = await Promise.all([
        fetch(`${API_BASE}/users`, { credentials: 'include', headers: authHeaders }),
        fetch(`${API_BASE}/business-units`, { credentials: 'include', headers: authHeaders }),
        fetch(`${API_BASE}/segments`, { credentials: 'include', headers: authHeaders })
      ]);

      const failed = [usersResponse, buResponse, segmentResponse].find((r) => !r.ok);
      if (failed) {
        const payload = await failed.json().catch(() => ({}));
        throw new Error(payload?.error?.message || 'Failed to load assignment data');
      }

      const [usersPayload, buPayload, segmentPayload] = await Promise.all([
        usersResponse.json(), buResponse.json(), segmentResponse.json()
      ]);

      setUsers(normalizeResponse(usersPayload));
      setBusinessUnits(normalizeResponse(buPayload));
      setSegments(normalizeResponse(segmentPayload));

      if (isEnterprise) {
        const [regionsResponse, subRegionsResponse] = await Promise.all([
          fetch(`${API_BASE}/regions`, { credentials: 'include', headers: authHeaders }),
          fetch(`${API_BASE}/sub-regions`, { credentials: 'include', headers: authHeaders })
        ]);
        if (regionsResponse.ok) setRegions(normalizeResponse(await regionsResponse.json()));
        if (subRegionsResponse.ok) setSubRegions(normalizeResponse(await subRegionsResponse.json()));
      }
    } catch (error) {
      setLoadError(getErrorMessage(error));
    }
  }, [isEnterprise, authHeaders]);

  useEffect(() => {
    if (tier === 'essentials') return;
    fetchInitialData();
  }, [fetchInitialData, tier]);

  useEffect(() => {
    if (!toast) return undefined;
    const timeoutId = window.setTimeout(() => { setToast(null); }, 3000);
    return () => window.clearTimeout(timeoutId);
  }, [toast]);

  useEffect(() => {
    if (tier === 'essentials') return undefined;
    const timeoutId = window.setTimeout(async () => {
      setIsLoadingPreview(true);
      try {
        const params = new URLSearchParams();
        params.set('count', 'true');
        if (buScope) params.set('business_unit_id', buScope);
        if (segmentScope) params.set('segment_id', segmentScope);
        const response = await fetch(`${API_BASE}/positions?${params.toString()}`, {
          credentials: 'include',
          headers: authHeaders
        });
        if (!response.ok) { setPositionPreviewCount(0); return; }
        const payload = await response.json();
        const count = payload?.count || payload?.data?.count || payload?.position_count || payload?.positionCount || 0;
        setPositionPreviewCount(Number(count));
      } catch (_error) {
        setPositionPreviewCount(0);
      } finally {
        setIsLoadingPreview(false);
      }
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [buScope, segmentScope, tier, authHeaders]);

  const toggleGeoNode = (node) => {
    const nodeKey = node.sub_region_id ? `sub_region:${node.sub_region_id}` : `region:${node.region_id}`;
    setGeoScope((current) => {
      const exists = current.some((item) => item.key === nodeKey);
      if (exists) return current.filter((item) => item.key !== nodeKey);
      return [...current, { key: nodeKey, region_id: node.region_id || null, sub_region_id: node.sub_region_id || null, label: node.label }];
    });
  };

  const saveAssignment = async () => {
    if (!canSave) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const assignmentResponse = await fetch(`${API_BASE}/user-role-assignments`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders,
        body: JSON.stringify({
          user_id: selectedUserId,
          role_name: 'comp_admin',
          business_unit_id: buScope ? Number(buScope) : null,
          segment_id: segmentScope ? Number(segmentScope) : null,
          effective_date: new Date().toISOString().slice(0, 10),
          assigned_by: user?.user_id || user?.id || null
        })
      });

      if (!assignmentResponse.ok) {
        const payload = await assignmentResponse.json().catch(() => ({}));
        throw new Error(payload?.error?.message || 'Failed to save domain assignment');
      }

      if (isEnterprise && geoScope.length > 0) {
        for (const node of geoScope) {
          if (Boolean(node.region_id) === Boolean(node.sub_region_id)) {
            throw new Error('Geo scope must include exactly one region or sub-region.');
          }
          const geoResponse = await fetch(`${API_BASE}/user-access-scope`, {
            method: 'POST',
            credentials: 'include',
            headers: authHeaders,
            body: JSON.stringify({
              user_account_id: selectedUserId,
              region_id: node.region_id,
              sub_region_id: node.sub_region_id,
              created_by: user?.user_id || user?.id || null
            })
          });
          if (!geoResponse.ok) {
            const payload = await geoResponse.json().catch(() => ({}));
            throw new Error(payload?.error?.message || 'Failed to save geo scope');
          }
        }
      }
      setToast('Domain assignment saved.');
    } catch (error) {
      setSaveError(getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const geoNodes = useMemo(() => {
    const regionNodes = regions.map((region) => ({
      key: `region:${region.region_id}`, region_id: region.region_id, sub_region_id: null,
      label: region.region_name || region.region_code
    }));
    const subRegionNodes = subRegions.map((subRegion) => ({
      key: `sub_region:${subRegion.sub_region_id}`, region_id: null, sub_region_id: subRegion.sub_region_id,
      label: subRegion.sub_region_name || subRegion.sub_region_code
    }));
    return [...regionNodes, ...subRegionNodes];
  }, [regions, subRegions]);

  if (tier === 'essentials') {
    return (
      <section className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-8 shadow-sm">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">
          Domain assignment is not available on Essentials
        </h2>
        <p className="mt-2 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Essentials tenants continue directly to configure gate summary.
        </p>
        <button type="button" onClick={onContinue} className="mt-5 rounded-xl bg-[rgb(var(--color-primary))] px-5 py-2.5 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))]">
          Continue
        </button>
      </section>
    );
  }

  return (
    <section className="relative rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-sm">
      {toast && (
        <div className="absolute right-5 top-5 rounded-xl bg-[rgb(var(--color-success))] px-4 py-3 text-tok-sm font-medium text-white shadow-lg">
          {toast}
        </div>
      )}

      <div className="border-b border-[rgb(var(--color-border))] p-5">
        <p className="text-tok-sm font-medium text-[rgb(var(--color-text-muted))]">CONF-04</p>
        <h2 className="mt-1 text-tok-xl font-semibold text-[rgb(var(--color-text))]">Domain assignment</h2>
        <p className="mt-2 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Assign each comp admin to the business, segment, and optional geo scope they can administer.
        </p>
      </div>

      <div className="space-y-5 p-5">
        {loadError && (
          <div className="rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 p-4 text-tok-sm text-[rgb(var(--color-danger))]">
            <p className="font-semibold">Unable to load assignment data</p>
            <p className="mt-1">{loadError}</p>
            <button type="button" onClick={fetchInitialData} className="mt-3 rounded-lg bg-[rgb(var(--color-danger))] px-3 py-2 text-white">Retry</button>
          </div>
        )}

        <div className="grid gap-5 lg:grid-cols-3">
          <div>
            <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">Comp admin</label>
            <select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)} className="mt-2 w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm">
              <option value="">Select user</option>
              {users.map((item) => (
                <option key={item.user_id || item.id || item.email} value={item.user_id || item.id}>
                  {item.full_name || item.fullName || item.email}
                </option>
              ))}
            </select>
            {selectedUser && <p className="mt-2 text-tok-xs text-[rgb(var(--color-text-muted))]">{selectedUser.email}</p>}
          </div>

          <div>
            <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">Business unit scope</label>
            <select value={buScope} onChange={(e) => setBuScope(e.target.value)} className="mt-2 w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm">
              <option value="">All business units</option>
              {businessUnits.map((bu) => (
                <option key={bu.business_unit_id} value={bu.business_unit_id}>{bu.bu_name || bu.bu_code}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">Segment scope</label>
            <select value={segmentScope} onChange={(e) => setSegmentScope(e.target.value)} className="mt-2 w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm">
              <option value="">All segments</option>
              {segments.map((segment) => (
                <option key={segment.segment_id} value={segment.segment_id}>{segment.segment_name || segment.segment_code}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4">
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Position preview</p>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
            {isLoadingPreview ? 'Loading preview...' : `${positionPreviewCount} positions match the selected scope.`}
          </p>
        </div>

        {isEnterprise && (
          <div className="rounded-xl border border-[rgb(var(--color-border))] p-4">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Enterprise geo scope</h3>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              Select regions or sub-regions. Each saved geo scope row must contain exactly one region or one sub-region.
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {geoNodes.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] p-4 text-tok-sm text-[rgb(var(--color-text-muted))]">
                  No geo nodes available.
                </div>
              ) : (
                geoNodes.map((node) => {
                  const checked = geoScope.some((item) => item.key === node.key);
                  return (
                    <label key={node.key} className="flex items-center gap-3 rounded-xl border border-[rgb(var(--color-border))] p-3 text-tok-sm text-[rgb(var(--color-text))]">
                      <input type="checkbox" checked={checked} onChange={() => toggleGeoNode(node)} />
                      <span>{node.label}</span>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        )}

        {saveError && (
          <div className="rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 p-4 text-tok-sm text-[rgb(var(--color-danger))]">
            {saveError}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] p-5">
        <button type="button" onClick={onBack} className="rounded-xl border border-[rgb(var(--color-border))] px-5 py-2.5 text-tok-sm font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]">
          Back
        </button>
        <div className="flex gap-3">
          <button type="button" disabled={!canSave || isSaving} onClick={saveAssignment} className="rounded-xl border border-[rgb(var(--color-border))] px-5 py-2.5 text-tok-sm font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:cursor-not-allowed disabled:opacity-50">
            {isSaving ? 'Saving...' : 'Save Assignment'}
          </button>
          <button type="button" onClick={onContinue} className="rounded-xl bg-[rgb(var(--color-primary))] px-5 py-2.5 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))]">
            Continue
          </button>
        </div>
      </div>
    </section>
  );
}
